<template>
	<span
		v-bind="$attrs"
		class="pronto-icon"
		:class="{
			'fixed-width': fixedWidth,
			spin,
			'pronto-icon-backdrop': backdrop,
			[`pronto-icon-${type}`]: true,
			'pronto-icon-halo': halo,
		}"
	>
		<slot :type="type" />
		<IconBackdrop v-if="backdrop" class="backdrop" />
	</span>
</template>

<script>
import IconBackdrop from './IconBackdrop'

export const types = {
	SOLID: 'solid',
	REGULAR: 'regular',
	LIGHT: 'light',
	THIN: 'thin',
	DUOTONE: 'duotone',
}

// TODO: change default to REGULAR as that is the new UX preference
export const props = {
	spin: { type: Boolean, default: null },
	fixedWidth: { type: Boolean, default: null },
	halo: { type: Boolean, default: null },
	backdrop: { type: Boolean, default: null },
	type: {
		type: String,
		default: types.LIGHT,
		validator: (val) => Object.values(types).includes(val),
	},
}

export const lightProps = {
	...props,
	type: {
		...props.type,
		default: types.LIGHT,
	},
}

export const regularProps = {
	...props,
	type: {
		...props.type,
		default: types.REGULAR,
	},
}

export const solidProps = {
	...props,
	type: {
		...props.type,
		default: types.SOLID,
	},
}

export default {
	name: 'Icon',
	components: { IconBackdrop },
	props,
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';
// How to style:
//    - Size: use "font-size: 18px;"
//    - Color: use "color: red;"
// Valid css vars are:
//    --rotate: the angle to rotate the svg
//    --duration: the transition duration
//    --spin-duration: time for svg to spin 1 full turn

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(1turn);
	}
}

.pronto-icon {
	display: inline-flex;
	align-items: center;
	position: relative;
	width: 1em;
	height: 1em;
	&.fixed-width {
		:deep(svg) {
			text-align: center;
			width: 1.25em;
		}
	}
	&.spin {
		:deep(svg) {
			animation: spin var(--spin-duration, 1.5s) linear infinite;
		}
	}
	:deep(svg:not(.backdrop)) {
		width: 1em;
		height: 1em;
		transform: rotate(var(--rotate, 0deg));
		transition: all var(--duration, 0.3s) linear;
		transition-property: transform;
		> path {
			display: none;
		}
	}
	&.pronto-icon-solid {
		:deep(svg) > .solid {
			display: inline;
		}
	}
	&.pronto-icon-regular {
		:deep(svg) > .regular {
			display: inline;
		}
	}
	&.pronto-icon-light {
		:deep(svg) > .light {
			display: inline;
		}
	}
	&.pronto-icon-thin {
		:deep(svg) > .thin {
			display: inline;
		}
	}
	&.pronto-icon-duotone {
		:deep(svg) > .duotone {
			display: inline;
		}
	}
	&.pronto-icon-halo {
		$halo-size: 1.8em;
		$half-halo: 0.9em;
		width: $halo-size;
		height: $halo-size;
		@include flex-center;
		&::before {
			content: '';
			width: $halo-size;
			height: $halo-size;
			border-radius: $half-halo;
			overflow: hidden;
			position: absolute;
			background-color: currentColor;
			opacity: 0.1;
		}
	}
	&.pronto-icon-backdrop {
		position: relative;
		.backdrop {
			color: #f3f4f5;
			z-index: 0;
			position: absolute;
			left: -66%;
			height: 0.9em;
			width: auto;
		}
		:deep(svg) {
			z-index: 1;
		}
	}
	:deep(svg) {
		path {
			fill: currentColor;
		}
	}
}
</style>
